<template>
  <div>
    <div class="d-flex button-group"
         :class="{'justify-content-end': isNewSalesOrder, 'justify-content-between': !isNewSalesOrder}">
      <b-button class="history-btn" v-if="!isNewSalesOrder" @click="openHistoryModal">
        {{$t('buttons.common.history')}}
      </b-button>
      <history-modal-content></history-modal-content>
      <div class="button-group margin-0 d-flex" v-if="currentRoutePermissions.change_salesorder">
        <div class="button-group" v-if="!isNewSalesOrder">
          <b-btn variant="primary" size="sm"
                 :class="{'hidden': isNewSalesOrder || !isLoadedData}"
                 id="saveAsNewOrder">
            {{$t('buttons.common.save_as_new')}}
          </b-btn>
          <b-btn variant="primary" size="sm"
                 :class="{'hidden': isNewSalesOrder || !isLoadedData}"
                 :disabled="!salesOrderData.is_unshipped"
                 id="saveAsNewLastOrder">
            {{$t('buttons.common.save_as_new_last')}}
          </b-btn>
          <b-btn variant="success" size="sm" v-if="salesOrderStatus === 'final' && isLoadedData"
                 @click="changeOrderStatus('completed')">
            {{$t('buttons.common.complete')}}
          </b-btn>
          <b-btn variant="success" size="sm" v-if="salesOrderStatus === 'forecast' && isLoadedData"
                 @click="changeOrderStatus('final')">
            {{$t('buttons.common.confirm')}}
          </b-btn>
          <b-btn variant="warning" size="sm" v-if="!isEndedOrder && isLoadedData"
                 @click="cancelOrder()">
            {{$t('buttons.common.cancel')}}
          </b-btn>
          <b-btn size="sm" v-if="isEndedOrder && isLoadedData" @click="recoverOrder">
            {{$t('buttons.common.recover')}}
          </b-btn>
        </div>
        <b-btn variant="outline-success" size="sm" @click="save" v-if="!isEndedOrder && isLoadedData">
          {{$t('buttons.common.save')}}
        </b-btn>
      </div>
    </div>
    <b-popover target="saveAsNewOrder" placement="bottom" :show.sync="popoverSaveAsNewShow">
      <b-btn variant="success" size="sm" @click="saveAsNewOrder('final')">Sales Order</b-btn>
      <b-btn variant="warning" size="sm" @click="saveAsNewOrder('forecast')">Forecast Order</b-btn>
    </b-popover>
     <b-popover target="saveAsNewLastOrder" placement="bottom" :show.sync="popoverSaveAsNewLastShow">
      <b-btn variant="success" size="sm" @click="saveAsNewLastOrder('final')">Sales Order</b-btn>
      <b-btn variant="warning" size="sm" @click="saveAsNewLastOrder('forecast')">Forecast Order</b-btn>
    </b-popover>
    <div class="d-flex justify-content-between toggle-container" @click="showCollapse = !showCollapse"
         :aria-expanded="showCollapse ? 'true' : 'false'"
         aria-controls="collapse">
      <h3>
        {{salesOrderLabel}}
      </h3>
      <div class="icons-wrapper">
        {{salesOrderInfo}}
        <icon name="angle-down" class="angle" v-if="showCollapse"></icon>
        <icon name="angle-right" class="angle" v-else></icon>
      </div>
    </div>
    <sales-order-position-modal :id="this.param">
    </sales-order-position-modal>
    <div>
      <b-collapse id="collapse" v-model="showCollapse">
        <div class="filter-form">
          <b-row>
            <b-col>
              <label>{{$t('labels.sale_contract.sale_contract')}}</label>
              <multiselect v-model="salesOrderData.sales_contract" :max-height="200"
                           placeholder="" label="contract_nr" track-by="contract_nr"
                           :options="salesContracts"
                           :close-on-select="true" @select="selectSalesContract"
                           :disabled="isEndedOrder"
              ></multiselect>
            </b-col>
            <b-col>
              <label>{{$t('labels.common.currency')}}</label>
              <multiselect v-model="salesOrderData.currency" :max-height="200"
                           placeholder="" label="code" track-by="code"
                           :options="currencies"
                           :class="{ required: ((!salesOrderData.currency) && isLoadedData)}"
                           :close-on-select="true" :allowEmpty="false" :show-labels="false"
                           :disabled="isEndedOrder"></multiselect>
            </b-col>
            <b-col>
              <label>{{$t('labels.common.incoterms')}}</label>
              <multiselect v-model="salesOrderData.incoterms" :max-height="200"
                           placeholder="" label="code" track-by="code"
                           :options="incoterms" :searchable="true"
                           :close-on-select="true" :disabled="isEndedOrder"></multiselect>
            </b-col>
          </b-row>
          <b-row>
            <b-col>
              <label>{{$t('labels.common.buyer')}}</label>
              <multiselect v-model="salesOrderData.partner" :max-height="200"
                           placeholder="" label="name" track-by="name"
                           :options="buyers"
                           :class="{ required: ((!salesOrderData.partner) && isLoadedData)}"
                           :close-on-select="true" :allowEmpty="false" @select="selectBuyer"
                           :disabled="isEndedOrder">
              </multiselect>
            </b-col>
            <b-col>
              <label>{{$t('labels.common.payment_terms')}}</label>
              <div class="d-flex">
                <multiselect v-model="salesOrderData.payment_term" :max-height="200"
                             placeholder="" label="short_name" track-by="short_name"
                             :options="payment_terms" :searchable="true"
                             :class="{ required: ((!salesOrderData.payment_term) && isLoadedData)}"
                             :close-on-select="true" :allowEmpty="false"
                             :show-labels="false" :disabled="isEndedOrder"></multiselect>
                <div class="icons-group vertical-center-container">
                  <icon name="plus-circle" id="add-payment-term" :disabled="isEndedOrder"></icon>
                </div>
              </div>
              <b-popover class="p-2" target="add-payment-term" placement="right" title="Add payment term"
                         :show.sync="popoverPaymentTermShow">
                <template slot="title">
                  <div class="d-flex justify-content-between">
                    <span class="align-self-end">Add payment term</span>
                    <span @click="closePaymentTermPopover"
                          class="d-inline-block close-popover-icon align-self-end"
                          aria-hidden="true">&times;</span>
                  </div>
                </template>
                <b-row>
                  <b-col>
                    <label>{{$t('labels.common.type')}}</label>
                    <select v-model="addPaymentTermData.selectedType" class="form-control">
                      <option v-for="item in addPaymentTermData.payment_term_types"
                              :key="item.type" :value="item">{{item.text}}
                      </option>
                    </select>
                  </b-col>
                </b-row>
                <div class="note">
                  {{addPaymentTermData.selectedType && addPaymentTermData.selectedType.note}}
                </div>
                <b-row>
                  <b-col>
                    <label>{{$t('labels.sale_order.short_name')}}</label>
                    <input class="form-control" v-model="addPaymentTermData.short_name"
                           :class="{ required: !addPaymentTermData.short_name}">
                  </b-col>
                </b-row>
                <b-row v-if="addPaymentTermData.selectedType && addPaymentTermData.selectedType.type === 'net_n_days'">
                  <b-col>
                    <label>{{$t('labels.sale_order.full_name')}}</label>
                    <input class="form-control" v-model="addPaymentTermData.full_name">
                  </b-col>
                </b-row>
                <b-row v-if="addPaymentTermData.selectedType && addPaymentTermData.selectedType.type === 'net_n_days'">
                  <b-col>
                    <label>{{$t('labels.common.value')}}</label>
                    <input class="form-control" v-model="addPaymentTermData.value"
                           :class="{ required: !addPaymentTermData.value}">
                  </b-col>
                </b-row>
                <b-row>
                  <b-col>
                    <label>{{$t('labels.common.notes')}}</label>
                    <input class="form-control" v-model="addPaymentTermData.notes">
                  </b-col>
                </b-row>
                <b-row class="d-flex justify-content-between margin-0">
                  <b-btn variant="success" size="sm" @click="addPaymentTerm">Add</b-btn>
                  <img src="@/assets/images/common-icons/icon_clear.png"
                       :title="'Clear payment term form'" @click="clearPaymentTermData">
                </b-row>
              </b-popover>
            </b-col>
            <b-col>
              <label>{{$t('labels.common.delivery_place')}}</label>
              <multiselect v-model="salesOrderData.partner_shipping" :max-height="200"
                           placeholder="" label="name" track-by="name"
                           :options="partner_shipping_adresses" :searchable="true"
                           :multiple="false" :close-on-select="true" :disabled="isEndedOrder"
              ></multiselect>
            </b-col>
          </b-row>
          <b-row>
            <b-col>
              <label>{{$t('labels.sale_order.requisition_nr')}}</label>
              <input class="form-control" v-model="salesOrderData.requisition_number"
                     placeholder="" :disabled="isEndedOrder">
            </b-col>
            <b-col>
              <label>{{$t('labels.sale_order.text_notes')}}</label>
              <multiselect v-model="salesOrderData.additional_information" :max-height="200"
                           placeholder="" label="name" track-by="name"
                           :options="invoice_additional_info_list" :searchable="true"
                           :multiple="true" :close-on-select="false" :disabled="isEndedOrder"
              ></multiselect>
            </b-col>
            <b-col>
              <label>{{$t('labels.sale_order.receiver_of_the_load')}}</label>
              <multiselect v-model="salesOrderData.receiver_of_the_load" :max-height="200"
                           placeholder="" label="name" track-by="name"
                           :options="delivery_addresses" :searchable="true"
                           :multiple="false" :close-on-select="true" :disabled="isEndedOrder"
              ></multiselect>
            </b-col>
          </b-row>
          <b-row>
            <b-col>
              <label>{{$t('labels.sale_order.order_nr')}}</label>
              <input class="form-control" v-model="salesOrderData.order_number"
                     placeholder="" :disabled="isEndedOrder">
            </b-col>
            <b-col>
              <label>{{$t('labels.sale_order.invoice_generation')}}</label>
              <multiselect v-model="salesOrderData.invoice_creating_type" :max-height="200"
                           placeholder="" label="name" track-by="name"
                           :options="invoice_creating_types" :searchable="true"
                           :class="{ required: ((!salesOrderData.invoice_creating_type) && isLoadedData)}"
                           :allowEmpty="false" :show-labels="false"
                           :close-on-select="true" :disabled="isEndedOrder"></multiselect>
            </b-col>
            <b-col>
              <label>{{$t('labels.common.delivery_method')}}</label>
              <multiselect v-model="salesOrderData.delivery_method" :max-height="200"
                           placeholder="" label="name" track-by="name"
                           :options="delivery_methods" :searchable="true"
                           :close-on-select="true" :disabled="isEndedOrder"></multiselect>
            </b-col>
          </b-row>
          <b-row>
            <b-col>
              <label>{{$t('labels.common.discount')}}, %</label>
              <input class="form-control" v-replace-with-negative
                     v-model.number="salesOrderData.discount_invoice_value"
                     :class="{ required: ((salesOrderData.discount_invoice_value === '') && isLoadedData)}"
                     :disabled="isEndedOrder">
            </b-col>
            <b-col>
              <label>{{$t('labels.invoices.invoice_type')}}</label>
              <multiselect v-model="salesOrderData.invoicing_type" :max-height="200"
                           placeholder="" label="name" track-by="name"
                           :options="invoicing_types" :searchable="true"
                           :class="{ required: ((!salesOrderData.invoicing_type) && isLoadedData)}"
                           :allowEmpty="false" :show-labels="false"
                           :close-on-select="true" :disabled="isEndedOrder"></multiselect>
            </b-col>
            <b-col>
              <label>{{$t('labels.common.delivery_date')}}</label>
              <datepicker v-model="salesOrderData.delivery_date" lang="en"
                          placeholder=""
                          :clearable="false"
                          :class="{ required: ((!salesOrderData.delivery_date) && isLoadedData)}"
                          :allowEmpty="false" :disabled="isEndedOrder" :first-day-of-week="1"
              ></datepicker>
            </b-col>
          </b-row>
          <b-row>
            <b-col>
              <label>{{$t('labels.common.seller')}}</label>
              <multiselect v-model="salesOrderData.partner_seller" :max-height="200"
                           placeholder="" label="name" track-by="name"
                           :options="sellers"
                           :class="{ required: ((!salesOrderData.partner_seller) && isLoadedData)}"
                           :close-on-select="true" :allowEmpty="false" :show-labels="false"
                           :disabled="isEndedOrder"></multiselect>
            </b-col>
            <b-col cols="4">
              <label>{{$t('labels.sale_order.producer_service')}}</label>
              <multiselect v-model="salesOrderData.additional_partner" :max-height="200"
                           placeholder="" label="name" track-by="name"
                           :options="partners" :searchable="true"
                           :class="{ required: ((!salesOrderData.additional_partner) && isLoadedData && !isInvoicingTypeEqualDirect)}"
                           :close-on-select="true"
                           :disabled="isEndedOrder || isInvoicingTypeEqualDirect"></multiselect>
            </b-col>
            <b-col class="checkbox-wrapper">
              <label for="virtual_stock">{{$t('labels.common.virtual_stock')}}</label>
              <input id="virtual_stock" type="checkbox" v-model="salesOrderData.virtual_stock" :disabled="isEndedOrder">
            </b-col>
          </b-row>
          <b-row>
            <b-col cols="4" offset="8">
              <label>{{$t('labels.common.notes')}}</label>
              <b-form-textarea v-model="salesOrderData.notes" placeholder=""
                               :disabled="isEndedOrder">
              </b-form-textarea>
            </b-col>
          </b-row>
        </div>
      </b-collapse>
    </div>
    <div v-if="!isNewSalesOrder">
      <b-card no-body>
        <b-tabs card>
          <b-tab active @click="loadSalesOrderItems">
            <template slot="title">
              <portal-target name="products_tab"></portal-target>
            </template>
            <portal to="products_tab">{{$t('tabs.sale_order.products')}}</portal>
            <b-row>
              <b-col class="d-flex justify-content-between">
                <div class="d-flex">
                  <div class="counter">
                    {{$t('labels.sale_order.selected_items')}}: {{selectedSalesOrderItems.length}}
                  </div>
                  <div class="button-group" v-if="currentRoutePermissions.change_salesorder">
                    <b-btn variant="outline-success" size="sm"
                           @click="openSalesContractPositionModal"
                           :disabled="isEndedOrder || isThereUniversal">
                      {{$t('buttons.sale_contract.add_position')}}
                    </b-btn>
                    <b-btn variant="outline-danger" size="sm" @click="deleteSalesOrderItems"
                           :disabled="isEndedOrder">
                      {{$t('buttons.sale_contract.delete_position')}}
                    </b-btn>
                  </div>
                </div>
                <div>
                  <save-columns-btn :grid="salesOrderItemsGrid" :grid-name="salesOrderItemsGridName"
                                    class="margin-left-5"></save-columns-btn>
                </div>
              </b-col>
            </b-row>
            <b-row>
              <b-col>
                <ag-grid-vue class="ag-theme-balham" :style="gridHeight"
                             :columnDefs="salesOrderItemsColumns"
                             :gridOptions="salesOrderItemsGrid"
                             :rowData="salesOrderItems"
                             @selectionChanged="selectionChangeSalesOrderItemsGrid"
                             @rowDoubleClicked="loadSalesOrderItemData"
                             @rowDataChanged="resetSelectedRows"
                             :postProcessPopup="postProcessPopup">
                </ag-grid-vue>
              </b-col>
            </b-row>
            <b-popover
                :key="id"
                v-for="{ id } in repackedSalesOrderItems"
                v-if="salesOrderItems.length"
                :target="`salesOrderExclamatory${id}`"
                triggers="hover"
                placement="top">
              {{$t('labels.sale_order.packaging_product_dont_match')}}
            </b-popover>
          </b-tab>
          <b-tab @click="loadPacketsGroupingByShipments">
            <template slot="title">
              <portal-target name="shipment_tab"></portal-target>
            </template>
            <portal to="shipment_tab">{{$t('tabs.sale_order.shipment')}}</portal>
            <b-row>
              <b-col cols="2">
                {{$t('labels.sale_order.selected_packages')}} : {{shipmentData.selectedPackets.length}} <br/>
                {{$t('labels.sale_order.selected_m3')}} : {{shipmentData.selectedPacketsSumM3}}
              </b-col>
              <b-col class="vertical-center-container" cols="2">
                <b-btn v-if="currentRoutePermissions.change_salesorder" variant="success" size="sm"
                       @click="createInvoice"
                       :disabled="isAddInvoiceDisabled">
                  {{$t('buttons.sale_order.add_invoice')}}
                </b-btn>
                <move-btn-sales-order class="ml-2" @getSelected="getSelectedRows"/>
              </b-col>
              <b-col offset="3">
                <div v-for="item in salesOrderData.related_invoices" style="float:right"
                     class="margin-right-5" :key="item.id">
                  <router-link :to="`/sales-invoice/${item.id}`"
                     >{{item.invoice_number}}</router-link>
                  <span> / </span>
                </div>
              </b-col>
            </b-row>
            <b-row>
              <b-col>
                <ag-grid-vue class="ag-theme-balham" :style="gridHeight"
                             :columnDefs="shipmentsColumns"
                             :gridOptions="shipmentsGrid" :rowData="shipments"
                             @selectionChanged="selectionChangedShipmentsGrid"
                             :postProcessPopup="postProcessPopup">
                </ag-grid-vue>
              </b-col>
            </b-row>
          </b-tab>
          <b-tab @click="loadShippingSpecificationData">
            <template slot="title">
              <portal-target name="non-bounded-packages-count"></portal-target>
            </template>
            <b-row>
              <portal to="non-bounded-packages-count">
                {{$t('tabs.sale_order.shipping_specification')}}
                <b-badge variant="danger" v-if="nonBoundedPackagesCount && nonBoundedPackagesCount > 0">
                  {{nonBoundedPackagesCount}}
                </b-badge>
              </portal>
              <b-col cols="6">
                <h4>{{$t('labels.sale_order.sales_order_items')}}</h4>
              </b-col>
              <b-col offset="1" class="padding-left-0 d-flex justify-content-between">
                <h4>{{$t('labels.sale_order.non_bounded_packages')}}</h4>
                <b-btn variant="warning" size="sm" @click="distributePackages" :disabled="isEndedOrder">
                  {{$t('buttons.sale_order.distribute_packages')}}
                </b-btn>
              </b-col>
            </b-row>
            <b-row>
              <b-col cols="6" class="padding-right-0">
                <ag-grid-vue class="ag-theme-balham" :style="gridHeight"
                             :columnDefs="packagesGroupingByOrderItemsColumns"
                             :gridOptions="packagesGroupingByOrderItemsGrid"
                             :rowData="packagesGroupingByOrderItems"
                             :postProcessPopup="postProcessPopup"
                ></ag-grid-vue>
              </b-col>
              <b-col cols="1" class="arrows-wrapper">
                <div v-if="currentRoutePermissions.change_salesorder" @click="unboundPackages"
                     title="Unbind packages from order item"
                     :disabled="isEndedOrder">
                  <icon name="arrow-circle-right" class="bind-arrow"></icon>
                </div>
                <div v-if="currentRoutePermissions.change_salesorder" @click="boundPackages"
                     title="Bind packages to order item"
                     :disabled="isEndedOrder">
                  <icon name="arrow-circle-left" class="bind-arrow"></icon>
                </div>
              </b-col>
              <b-col class="padding-left-0">
                <ag-grid-vue class="ag-theme-balham" :style="gridHeight"
                             :columnDefs="nonBoundedPackagesColumns"
                             :gridOptions="nonBoundedPackagesGrid"
                             :rowData="nonBoundedPackages"
                             @rowDoubleClicked="loadSalesOrderItemDataFromPackage"
                             :postProcessPopup="postProcessPopup">
                </ag-grid-vue>
              </b-col>
            </b-row>
          </b-tab>
          <b-tab @click="loadDocuments(true)">
            <template slot="title">
              <portal-target name="documents_tab"></portal-target>
            </template>
            <portal to="documents_tab">{{$t('tabs.common.documents')}}</portal>
            <app-documents :isCollapse="showCollapse" :documents="documents" :url="`${commonUrl}attachments/`"
                           @loadDocuments="loadDocuments"></app-documents>
          </b-tab>
          <b-tab @click="loadComments(true)">
            <template slot="title">
              <portal-target name="comments_tab"></portal-target>
            </template>
            <portal to="comments_tab">{{$t('tabs.common.comments')}}</portal>
            <app-comments :comments="comments" :url="`${commonUrl}comments/`"
                          @loadComments="loadComments"></app-comments>
          </b-tab>
        </b-tabs>
      </b-card>
    </div>
  </div>
</template>

<script>
  import { AgGridVue } from 'ag-grid-vue';
  import { mapGetters, mapState } from 'vuex';
  import Multiselect from 'vue-multiselect';
  import Datepicker from 'vue2-datepicker';
  import moment from 'moment';
  import {
    handlerResponse,
    setNotifyData,
    getArrayByProperty,
  } from '@/helpers/commonHelpers';
  import { notificationsRenderer, autoResize } from '@/helpers/renderHelpers';
  import Comments from '@/components/common/Comments';
  import Documents from '@/components/common/Documents';
  import HistoryModalContent from '@/components/modals/HistoryModal';
  import SalesOrderPositionModal from '@/components/modals/SalesOrderPositionModal';
  import SaveColumnsBtn from '@/components/common/SaveColumnsBtn';
  import { gridMixins } from '@/mixins';
  import { DATE_FORMAT } from '@/constants';
  import MoveBtnSalesOrder from '@/components/common/sales-order/MoveBtnSalesOrder'

  export default {
    mixins: [gridMixins],
    components: {
      MoveBtnSalesOrder,
      Multiselect,
      Datepicker,
      'ag-grid-vue': AgGridVue,
      'app-comments': Comments,
      'app-documents': Documents,
      HistoryModalContent,
      SalesOrderPositionModal,
      SaveColumnsBtn
    },
    data() {
      return {
        showCollapse: false,
        isLoadedData: false,
        selectedSalesOrderItems: [],
        partner_shipping_adresses: [],
        shipmentData: {
          selectedPackets: [],
          selectedPacketsSumM3: 0,
          packetsBoundedWithInvoice: [],
          isFreePackages: false
        },
        popoverPaymentTermShow: false,
        popoverSaveAsNewShow: false,
        popoverSaveAsNewLastShow: false,
        addPaymentTermData: {
          selectedType: null,
          payment_term_types: [
            {
              text: 'Due upon receipt',
              type: 'due_upon_receipt',
              note: 'Full name will be coincide with Due Date(Due Date = Invoice Date)'
            },
            {
              text: 'Fixed payment terms',
              type: 'fixed_payment_terms',
              note: 'Full name will be coincide with Due date. Due Date must be selected from on invoice page'
            },
            {
              text: 'Net N days',
              type: 'net_n_days',
              note: 'Due Date will be calculated by adding N of days to Invoice Date'
            }
          ],
        },
        invoice_creating_types: [{name: 'Invoice to order', key: 'single_to_salesorder'}, {
          name: 'Invoice for loading',
          key: 'single_to_movement'
        }, {name: 'Custom', key: 'custom_invoice'}],
        invoicing_types: [{name: 'Direct', key: 'single'}, {name: 'Service', key: 'service'}, {
          name: 'Commission',
          key: 'commission'
        }],
        partnerForRoleAddingRoleAgent: null,
        salesOrderData: {
          descriptor: '',
          created_by: '',
          created: '',
          status: '',
          sales_contract: null,
          partner: null,
          requisition_number: '',
          order_number: '',
          partner_seller: null,
          delivery_date: null,
          incoterms: null,
          delivery_method: null,
          discount_invoice_value: 0,
          currency: null,
          payment_term: null,
          invoice_creating_type: null,
          invoice_type: null,
          additional_partner: null,
          notes: '',
          virtual_stock: false,
          additional_information: [],
          receiver_of_the_load: null
        }
      }
    },
    methods: {
      prepareSavedData() {
        return {
          additional_partner: this.salesOrderData.additional_partner && this.salesOrderData.additional_partner.id,
          create_discount_invoice: this.salesOrderData.create_discount_invoice,
          currency: this.salesOrderData.currency && this.salesOrderData.currency.id,
          delivery_date: this.salesOrderData.delivery_date && moment(this.salesOrderData.delivery_date).format(DATE_FORMAT),
          delivery_method: this.salesOrderData.delivery_method && this.salesOrderData.delivery_method.id,
          discount_invoice_value: this.salesOrderData.discount_invoice_value,
          incoterms: this.salesOrderData.incoterms && this.salesOrderData.incoterms.id,
          invoice_creating_type: this.salesOrderData.invoice_creating_type && this.salesOrderData.invoice_creating_type.key,
          invoicing_type: this.salesOrderData.invoicing_type && this.salesOrderData.invoicing_type.key,
          notes: this.salesOrderData.notes,
          order_number: this.salesOrderData.order_number,
          partner: this.salesOrderData.partner && this.salesOrderData.partner.id,
          partner_seller: this.salesOrderData.partner_seller && this.salesOrderData.partner_seller.id,
          partner_shipping: this.salesOrderData.partner_shipping && this.salesOrderData.partner_shipping.id,
          payment_term: this.salesOrderData.payment_term && this.salesOrderData.payment_term.id,
          requisition_number: this.salesOrderData.requisition_number,
          sales_contract: this.salesOrderData.sales_contract && this.salesOrderData.sales_contract.id,
          virtual_stock: this.salesOrderData.virtual_stock,
          status: this.salesOrderStatus,
          additional_information: getArrayByProperty(this.salesOrderData.additional_information, 'id'),
          receiver_of_the_load: this.salesOrderData.receiver_of_the_load && this.salesOrderData.receiver_of_the_load.id
        }
      },
      save() {
        const savedData = this.prepareSavedData();
        if (this.isNewSalesOrder) {
          this.$http.post(`sales/salesorders/`, savedData).then(handlerResponse).then((response) => {
            this.salesOrderData = {...this.salesOrderData, ...response};
            this.$router.push({path: `/sales-orders/${response.id}`});
            this.$notify(setNotifyData('Success', 'Sales Order has been created', 'success'));
          }).catch((error) => {
            console.log(error.data);
            notificationsRenderer(error.data, 'error');
          });
        } else {
          this.$http.put(`sales/salesorders/${this.param}/`, savedData).then(handlerResponse).then((response) => {
            this.salesOrderData = {...this.salesOrderData, ...response};
            this.$notify(setNotifyData('Success', 'Sales Order has been updated', 'success'));
          }).catch((error) => {
            console.log(error);
            notificationsRenderer(error.data, 'error');
          });
        }
      },
      changeOrderStatusCb(response) {
        this.salesOrderData = {...this.salesOrderData, ...response};
        const path = `/sales-orders/${response.id}`;
        (path !== this.$route.path) && this.$router.push({path});
        this.$notify(setNotifyData('Success', 'Sales Order status has been changed', 'success'));
      },
      recoverOrder() {
        this.$http.post(`sales/salesorders/${this.param}/move_status_back/`).then(handlerResponse).then((response) => {
          this.changeOrderStatusCb(response);
        }).catch((error) => {
          console.log(error);
          notificationsRenderer(error.data, 'error');
        });
      },
      changeOrderStatus(status) {
        this.$http.post(`sales/salesorders/${this.param}/move_status_forward/`).then(handlerResponse).then((response) => {
          this.changeOrderStatusCb(response);
        }).catch((error) => {
          console.log(error);
          notificationsRenderer(error.data, 'error');
        });
      },
      cancelOrder() {
        this.$http.post(`sales/salesorders/${this.param}/cancel/`).then(handlerResponse).then((response) => {
          this.changeOrderStatusCb(response);
        }).catch((error) => {
          console.log(error);
          notificationsRenderer(error.data, 'error');
        });
      },
      saveAsNewOrder(status) {
        this.popoverSaveAsNewShow = false;
        const savedData = this.prepareSavedData();
        this.$http.post(`sales/salesorders/${this.param}/save_as_new/`, {
          ...savedData,
          status
        }).then(handlerResponse).then((response) => {
          this.salesOrderData = {...this.salesOrderData, ...response};
          this.$router.push({path: `/sales-orders/${response.id}`});
          this.loadSalesOrderItems();
          this.$notify(setNotifyData('Success', 'Sales Order has been created', 'success'));
        })
      },
      saveAsNewLastOrder(status) {
        this.popoverSaveAsNewLastShow = false;
        const savedData = this.prepareSavedData();
        this.$http.post(`sales/salesorders/${this.param}/save_as_new_last/`, {
          ...savedData,
          status
        }).then(handlerResponse).then((response) => {
          this.salesOrderData = {...this.salesOrderData, ...response};
          this.$router.push({path: `/sales-orders/${response.id}`});
          this.loadSalesOrderItems();
          this.$notify(setNotifyData('Success', 'Sales Order has been created', 'success'));
        })
      },
      getSalesOrderInfo() {
        this.$http.get(`sales/salesorders/${this.param}/`).then(handlerResponse).then((response) => {
          this.salesOrderData = {...this.salesOrderData, ...response};
          this.$store.dispatch('location/setPathName', response.descriptor);
          this.fetchDeliveryContacts(this.salesOrderData.partner);
          this.isLoadedData = true;
        });
      },
      selectSalesContract(salesContract) {
        const params = {as_sales_order: true};
        this.$http.get(`sales/sales_contracts/${salesContract.id}/`, {params}).then(handlerResponse).then((response) => {
          delete response.status;
          this.salesOrderData.partner_shipping = null;
          this.salesOrderData = {...this.salesOrderData, ...response};
          this.fetchDeliveryContacts(this.salesOrderData.partner);
        })
      },
      selectBuyer(selectedBuyer) {
        this.salesOrderData.partner_shipping = null;
        this.fetchDeliveryContacts(selectedBuyer);
      },
      fetchDeliveryContacts(buyer) {
        const params = {
          partner_id: buyer.id,
          related_sales_contract: this.salesOrderData.sales_contract ? this.salesOrderData.sales_contract.id : ''
        };
        this.$http.get(`woodware/list/delivery_addresses/`, {params}).then(handlerResponse).then((response) => {
          this.partner_shipping_adresses = response;
        });
      },
      addPaymentTerm() {
        if (this.addPaymentTermData.selectedType) {
          const savedPaymentTermData = {
            type: this.addPaymentTermData.selectedType.type,
            short_name: this.addPaymentTermData.short_name ? this.addPaymentTermData.short_name : '',
            notes: this.addPaymentTermData.notes ? this.addPaymentTermData.notes : ''
          };
          if (this.addPaymentTermData.selectedType.type === 'net_n_days') {
            savedPaymentTermData['full_name'] = this.addPaymentTermData.full_name || '';
            savedPaymentTermData['value'] = this.addPaymentTermData.value || '';
          }
          this.$http.post(`sales/paymentterm/`, savedPaymentTermData).then(handlerResponse).then((response) => {
            this.closePaymentTermPopover();
            this.$notify(setNotifyData('', 'New Payment term has been added. The list of payment terms has been updated.', 'warn'));
            (async () => {
              await this.$store.dispatch('woodware/getPaymentTerms');
              this.salesOrderData.payment_term = response;
              this.closePaymentTermPopover();
            })();
          }).catch((error) => {
            notificationsRenderer(error.data, 'error');
          })
        }
      },
      closePaymentTermPopover() {
        this.popoverPaymentTermShow = false;
      },
      clearPaymentTermData() {
        this.addPaymentTermData.selectedType = null;
        delete this.addPaymentTermData.full_name;
        delete this.addPaymentTermData.value;
        delete this.addPaymentTermData.short_name;
        delete this.addPaymentTermData.notes;
      },
      selectionChangeSalesOrderItemsGrid() {
        this.selectedSalesOrderItems = this.salesOrderItemsGrid.api.getSelectedRows().filter((item) => item.id);
      },
      openSalesContractPositionModal(salesOrderItemData) {
        this.$root.$emit('openSalesOrderPositionModal', salesOrderItemData);
      },
      loadSalesOrderItemData(params) {
        if (this.currentRoutePermissions.change_salesorder) {
          if (!this.isEndedOrder) {
            const salesContractItemId = params.data.id;
            if (salesContractItemId) {
              this.$http.get(`sales/salesordersitems/${salesContractItemId}/`).then(handlerResponse).then((response) => {
                this.openSalesContractPositionModal(response);
              });
            }
          }
        }
      },
      resetSelectedRows() {
        this.selectedSalesOrderItems = [];
      },
      async loadSalesOrderItems() {
        await this.$store.dispatch('salesOrder/loadSalesOrderItems', {id: this.param});
        await this.$store.dispatch('salesOrder/loadNonBoundedPackages', {id: this.param});
      },
      deleteSalesOrderItems() {
        const id = getArrayByProperty(this.selectedSalesOrderItems, 'id');
        if (id.length) {
          this.$http.delete(`sales/salesorders/salesordersitems/`, {body: {id}}).then((response) => {
            this.$notify(setNotifyData('Success', response.body, 'success'));
          }).then(() => {
            this.loadSalesOrderItems();
            this.resetSelectedRows();
          }).catch((error) => {
            notificationsRenderer(error.body, 'error');
          });
        } else {
          this.$notify(setNotifyData('Select sales order for removing', '', 'warn'));
        }
      },
      resetShipmentInfo() {
        this.shipmentData.selectedPackets = [];
        this.shipmentData.selectedPacketsSumM3 = 0;
        this.shipmentData.packetsBoundedWithInvoice = [];
      },
      async loadPacketsGroupingByShipments() {
        await this.$store.dispatch('salesOrder/loadShipments', {id: this.param});
        await this.$store.dispatch('salesOrder/loadNonBoundedPackages', {id: this.param});
        this.resetShipmentInfo();
      },
      selectionChangedShipmentsGrid() {
        this.shipmentData.selectedPackets = this.shipmentsGrid.api.getSelectedRows().filter(item => !item.children)
        this.shipmentData.selectedPacketsSumM3 = this.shipmentData
            .selectedPackets
            .reduce((prev, next) => prev + next.m3, 0)
            .toFixed(3)
        this.shipmentData.packetsBoundedWithInvoice = getArrayByProperty(
            this.shipmentData.selectedPackets.filter(item => !item.invoice_item),
            'id'
        );
      },
      createInvoice() {
        const savedData = {
          additional_partner_id: this.salesOrderData.additional_partner && this.salesOrderData.additional_partner.id,
          discount_invoice_value: this.salesOrderData.discount_invoice_value,
          invoicing_type: this.salesOrderData.invoicing_type.key,
          movement_datas_id: this.shipmentData.packetsBoundedWithInvoice
        };
        this.$http.post(`accounting/invoices/create_invoice_to_movement_datas/`, savedData).then(handlerResponse).then((response) => {
          this.$store.dispatch('salesOrder/loadShipments', {id: this.param});
          const invoiceId = response.id;
          const currentUrl = window.location.origin;
          this.getSalesOrderInfo();
          window.open(`${currentUrl}/sales-invoice/${invoiceId}`, '_blank');
          this.resetShipmentInfo();
        }).catch(error => {
          notificationsRenderer(error.data, 'error');
        });
      },
      async loadShippingSpecificationData() {
        await this.$store.dispatch('salesOrder/loadPackagesGroupingByOrderItems', {id: this.param});
        await this.$store.dispatch('salesOrder/loadNonBoundedPackages', {id: this.param});
        this.$nextTick(() => {
          autoResize(this.nonBoundedPackagesGrid);
        });
      },
      boundPackages() {
        const selectedOrderItems = this.packagesGroupingByOrderItemsGrid.api.getSelectedRows().filter(item => item.children);
        const selectedNonBoundedPackages = this.nonBoundedPackagesGrid.api.getSelectedRows();
        if (selectedOrderItems.length === 1 && selectedNonBoundedPackages.length) {
          const movement_data = selectedNonBoundedPackages.map((item) => {
            return {soi_id: selectedOrderItems[0].id, id: item.id}
          });
          const savedData = {
            movement_data
          };
          this.$http.put(`sales/salesorders/${this.param}/salesordersitems/movementdata/`, savedData).then(handlerResponse).then((response) => {
            this.$notify(setNotifyData('Success', response, 'success'));
            this.loadShippingSpecificationData();
          }).catch((error) => {
            notificationsRenderer(error.data, 'error');
          });
        } else {
          this.$notify(setNotifyData('Select non bounded packages and just 1 order item', '', 'warn'));
        }
      },
      unboundPackages() {
        const selectedPackages = this.packagesGroupingByOrderItemsGrid.api.getSelectedRows();
        const movement_data = selectedPackages.filter(item => !item.children).map((item) => {
          return {id: item.id, soi_id: null}
        });
        if (movement_data.length) {
          const savedData = {movement_data};
          this.$http.put(`sales/salesorders/${this.param}/salesordersitems/movementdata/`, savedData).then(handlerResponse).then((response) => {
            this.$notify(setNotifyData('Success', response, 'success'));
            this.loadShippingSpecificationData();
          }).catch((error) => {
            notificationsRenderer(error.data, 'error');
          });
        } else {
          this.$notify(setNotifyData('Select just packages', '', 'warn'));
        }
      },
      distributePackages() {
        this.$http.post(`sales/salesorders/${this.param}/tie_packets`).then(handlerResponse).then((response) => {
          this.$notify(setNotifyData('Success', `Amount of distributed packages: ${response.tied_packets_amount}`, 'success'));
          this.loadShippingSpecificationData();
        }).catch((error) => {
          notificationsRenderer(error.data, 'error');
        });
      },
      loadSalesOrderItemDataFromPackage(params) {
        if (!this.isEndedOrder) {
          const packageId = params.data.id;
          this.$http.get(`sales/sales_order_item_data_from_package/${packageId}/`).then(handlerResponse).then((response) => {
            const packageData = {...response, ...{isPackageData: true}};
            this.openSalesContractPositionModal(packageData);
          }).catch((error) => {
            notificationsRenderer(error.data, 'error');
          });
        }
      },
      async fetchInitializeData() {
        this.$store.cache.dispatch('woodware/getDeliveryMethods');
        this.$store.dispatch('woodware/getPaymentTerms');
        this.$store.dispatch('woodware/getPartners', {is_active: true});
        this.$store.dispatch('woodware/getInvoiceAdditionalInfoList');
        this.$store.dispatch('partners/getSaleAgents');
        this.$store.dispatch('generic/loadIncoterms');
        this.$store.cache.dispatch('generic/getCurrencies');
        this.$store.dispatch('salesOrder/loadSalesContractsShort', {id: this.param});
        this.$store.cache.dispatch('woodware/getInvoiceAdditionalInfoList');
        this.$store.dispatch('woodware/getDeliveryAddresses');
        if (!this.isNewSalesOrder) {
          await this.getSalesOrderInfo();
          this.$store.commit('salesOrder/setShipments', []);
          this.loadSalesOrderItems();
          await this.$store.dispatch('salesOrder/loadNonBoundedPackages', {id: this.param});
        } else {
          this.isLoadedData = true;
          if (this.isNewSalesOrder) this.showCollapse = true;
        }
      },
      clearData() {
        if (this.comments.length) {
          this.$store.commit('salesOrder/setCommentsList', []);
        }
        if (this.documents.length) {
          this.$store.commit('salesOrder/setDocumentsList', []);
        }
        this.$store.cache.delete('salesOrder/loadDocumentsList', {id: this.param});
        this.$store.cache.delete('salesOrder/loadCommentsList', {id: this.param});
        this.$store.commit('salesOrder/setSalesOrderItems', {grid_name: '', data: []});
        this.$store.commit('salesOrder/setShipments', []);
        this.$store.commit('salesOrder/setPackagesGroupingByOrderItems', []);
        this.$store.commit('salesOrder/setNonBoundedPackages', []);
      },
      async loadComments(cacheFlag) {
        await this.$store.dispatch('salesOrder/loadNonBoundedPackages', {id: this.param});
        if (cacheFlag) {
          await this.$store.cache.dispatch('salesOrder/loadCommentsList', {id: this.param});
        } else {
          await this.$store.dispatch('salesOrder/loadCommentsList', {id: this.param});
        }
      },
      async loadDocuments(cacheFlag) {
        await this.$store.dispatch('salesOrder/loadNonBoundedPackages', {id: this.param});
        if (cacheFlag) {
          await this.$store.cache.dispatch('salesOrder/loadDocumentsList', {id: this.param});
        } else {
          await this.$store.dispatch('salesOrder/loadDocumentsList', {id: this.param});
        }
      },
      openHistoryModal() {
        this.$root.$emit('openHistoryModal', this.historySettings);
      },
      getSelectedRows (callback) {
        const selectedPackets = this.shipmentsGrid.api.getSelectedRows().filter(item => !item.children)
        const selectedMovements = this.shipmentsGrid.api.getSelectedRows().filter(item => item.children)
        callback({ selectedPackets, selectedMovements })
      }
    },
    computed: {
      ...mapState('salesOrder', ['salesOrderItemsGridName']),
      ...mapGetters('salesOrder', {
        salesContracts: 'salesContractsShort',
        salesOrderItems: 'salesOrderItems',
        salesOrderItemsColumns: 'salesOrderItemsColumns',
        salesOrderItemsGrid: 'salesOrderItemsGrid',
        shipments: 'shipments',
        shipmentsColumns: 'shipmentsColumns',
        shipmentsGrid: 'shipmentsGrid',
        packagesGroupingByOrderItemsGrid: 'packagesGroupingByOrderItemsGrid',
        packagesGroupingByOrderItems: 'packagesGroupingByOrderItems',
        packagesGroupingByOrderItemsColumns: 'packagesGroupingByOrderItemsColumns',
        nonBoundedPackagesGrid: 'nonBoundedPackagesGrid',
        nonBoundedPackages: 'nonBoundedPackages',
        nonBoundedPackagesColumns: 'nonBoundedPackagesColumns',
        comments: 'comments',
        documents: 'documents',
      }),
      ...mapGetters('woodware', {
        partners: 'partners',
        buyers: 'buyers',
        sellers: 'sellers',
        payment_terms: 'payment_terms',
        delivery_methods: 'delivery_methods',
        delivery_addresses: 'delivery_addresses',
        invoice_additional_info_list: 'invoice_additional_info'
      }),
      ...mapGetters('partners', {
        agents: 'agents'
      }),
      ...mapState('generic', ['currencies', 'incoterms', 'currentRoutePermissions']),
      isThereUniversal () {
        const items = this.salesOrderItems.filter(({ id }) => !!id)
        return items.some(({ universal }) => universal)
      },
      isAddInvoiceDisabled () {
        return !this.shipmentData?.selectedPackets.length || !this.shipmentData?.packetsBoundedWithInvoice.length || this.salesOrderStatus === 'canceled'
      },
      repackedSalesOrderItems () {
        return this.salesOrderItems.filter(item => item.is_repacked);
      },
      param() {
        return this.$route.params.id;
      },
      isNewSalesOrder() {
        return this.param === 'new';
      },
      commonUrl() {
        return `sales/salesorders/${this.param}/`;
      },
      salesOrderStatus() {
        return !this.isNewSalesOrder ? this.salesOrderData.status : this.$route.params.status;
      },
      isEndedOrder() {
        return this.salesOrderStatus === 'completed' || this.salesOrderStatus === 'canceled';
      },
      salesOrderLabel() {
        return this.isNewSalesOrder ? `${this.$i18n.t('common_content.new')} ${this.$i18n.t('common_content.sales_order')} - ${this.salesOrderStatus}`
          : `${this.$i18n.t('common_content.sales_order')} / ${this.salesOrderData.descriptor} - ${this.salesOrderStatus}`;
      },
      salesOrderInfo() {
        return this.isNewSalesOrder ? '' :
          `${this.salesOrderData.created} / ${this.$i18n.t('common_content.creator')} : ${this.salesOrderData.created_by}`
      },
      nonBoundedPackagesCount() {
        return this.nonBoundedPackages && this.nonBoundedPackages.length;
      },
      isInvoicingTypeEqualDirect() {
        return this.salesOrderData.invoicing_type && this.salesOrderData.invoicing_type.key === 'single';
      },
      gridHeight() {
        if (window.innerHeight > 1024) {
          return this.showCollapse ? {height: 'calc(100vh - 760px)'} : {height: 'calc(100vh - 300px)'};
        } else {
          return this.showCollapse ? {height: '400px'} : {height: 'calc(100vh - 300px)'};
        }
      },
      historySettings() {
        return {url: `sales_order/?id=${this.param}`, title: this.salesOrderLabel.split('/')[1]}
      }
    },
    created() {
      this.fetchInitializeData();
    },
    beforeRouteUpdate(to, from, next) {
      next();
      this.fetchInitializeData();
    },
    beforeRouteEnter(to, from, next) {
      next(vm => {
        vm.clearData();
      });
    }
  }
</script>

<style scoped lang="sass">

  .note
    margin-top: 5px
    font-size: 12px

  .close-popover-icon
    cursor: pointer
    font-size: 20px

  .counter
    padding-top: 3px

  .vue-portal-target
    display: inline-block

</style>
<style lang="sass">
.sales_order_exclamatory
  &__text
    margin-left: 5px
    &--no-mark
      margin-left: 15px
  &__mark
    color: red
    font-size: 30px
    width: 100%
    float: left
    max-width: 10px
    text-align: center
    &--in-progress
      position: absolute
      left: 7px
      z-index: 1
      max-width: 10px
      width: 100%
      color: red
      text-align: center
      font-size: 30px
</style>




// WEBPACK FOOTER //
// src/components/pages/modules/sales/orders/SalesOrder.vue